import * as Msal from 'msal'
import { captureException } from 'logrocket'
import {
  storeItem,
  getItem,
  clearItem
} from './local-storage'

const ScopeUri = `https://bakerbookhouse.onmicrosoft.com/${process.env.B2C_ENV}-bbh-ecomm-api/`
const Scopes = [
  'user_impersonate'
]
const B2C_SCOPES = Scopes.map(s => ScopeUri + s)

// Storage keys
const LAST_USED_AUTHORITY = 'last_used_authority'
const AUTH_STATE_KEY = 'msal_auth_in_progress'
const FORCE_AUTH_CALLBACK = 'force_auth_on_callback'

let msalApp
let tokenAccess

const initUserAgent = (authority) => {
  storeItem(LAST_USED_AUTHORITY, authority)
  const userAgentApplication = new Msal.UserAgentApplication({
    auth: {
      clientId: process.env.B2C_CLIENT_ID,
      authority: authority || process.env.B2C_AUTHORITY,
      navigateToLoginRequestUrl: false,
      postLogoutRedirectUri: process.env.APP_URL,
      validateAuthority: false,
      redirectUri: process.env.APP_URL + '/auth/callback'
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true // Changed to true for Safari compatibility
    },
    system: {
      loadFrameTimeout: 90000
    }
  })
  userAgentApplication.handleRedirectCallback(acquireTokenRedirectCallBack)
  return userAgentApplication
}

export const azureADSignIn = (authority = null) => {
  // Prevent multiple simultaneous auth attempts
  if (getItem(AUTH_STATE_KEY)) {
    console.log('Authentication already in progress')
    return
  }

  // Set flag to prevent loops
  storeItem(AUTH_STATE_KEY, 'true')

  const value = authority || process.env.B2C_AUTHORITY
  msalApp = initUserAgent(value)

  try {
    msalApp.loginRedirect({ scopes: B2C_SCOPES })
  } catch (error) {
    // Clear flag if redirect fails
    clearItem(AUTH_STATE_KEY)
    console.error('Login redirect failed:', error)
    captureException(error)
  }
}

export const acquireTokenSilent = async () => {
  const authority = getItem(LAST_USED_AUTHORITY)
  if (!msalApp) {
    msalApp = initUserAgent(authority)
    clearItem(LAST_USED_AUTHORITY)
  }

  const request = { scopes: B2C_SCOPES }

  return await msalApp.acquireTokenSilent(request).then((response) => {
    return response
  })
    .catch((error) => {
      console.log('Silent token acquisition failed, attempting redirect')
      console.log(error)

      // Check if auth is already in progress to prevent loops
      if (getItem(AUTH_STATE_KEY)) {
        console.log('Authentication already in progress, skipping redirect')
        return tokenAccess
      }

      // Set flag before redirect
      storeItem(AUTH_STATE_KEY, 'true')

      try {
        msalApp.acquireTokenRedirect(request)
      } catch (redirectError) {
        clearItem(AUTH_STATE_KEY)
        console.error('Token redirect failed:', redirectError)
        captureException(redirectError)
      }

      return tokenAccess
    })
}

export const azureADSignOut = () => {
  // Clear any auth state before logout
  clearItem(AUTH_STATE_KEY)
  clearItem(FORCE_AUTH_CALLBACK)

  if (!msalApp) {
    msalApp = initUserAgent()
  }

  try {
    msalApp.logout()
  } catch (error) {
    console.error('Logout failed:', error)
    captureException(error)
  }
}

export const acquireTokenRedirectCallBack = (error, response) => {
  console.log('Authentication callback triggered')
  console.log('Error:', error)
  console.log('Response:', response)

  // Always clear the auth state flag first
  clearItem(AUTH_STATE_KEY)

  if (error) {
    console.error('Authentication error:', error)

    // Handle password reset flow
    if (error.errorMessage && error.errorMessage.includes('AADB2C90118')) {
      storeItem(FORCE_AUTH_CALLBACK, true)
      const forgotPassAuthority = process.env.B2C_FORGOT_PASS_URL
      msalApp = initUserAgent(forgotPassAuthority)

      // Set flag again for password reset flow
      storeItem(AUTH_STATE_KEY, 'true')

      try {
        msalApp.loginRedirect()
      } catch (redirectError) {
        clearItem(AUTH_STATE_KEY)
        console.error('Password reset redirect failed:', redirectError)
        captureException(redirectError)
      }
      return
    }

    // Handle user cancellation - don't retry
    if (error.errorCode === 'user_cancelled' ||
        error.errorMessage?.includes('cancelled') ||
        error.errorMessage?.includes('AADB2C90091')) {
      console.log('User cancelled authentication')
      clearItem(FORCE_AUTH_CALLBACK)
      return
    }

    // Handle interaction required errors
    if (error.errorCode === 'interaction_required' ||
        error.errorCode === 'consent_required' ||
        error.errorCode === 'login_required') {
      console.log('Interaction required, user needs to sign in again')
      clearItem(FORCE_AUTH_CALLBACK)
      return
    }

    // Log other errors but don't retry to prevent loops
    console.error('Unhandled authentication error:', error)
    captureException(error)
    clearItem(FORCE_AUTH_CALLBACK)
    return
  }

  // Success case
  if (response) {
    console.log('Authentication successful')
    tokenAccess = response
    clearItem(FORCE_AUTH_CALLBACK)

    // Extract and track user email
    const email = response.idTokenClaims?.emails ? response.idTokenClaims.emails[0] : null
    if (email) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'userLoggedIn',
        userEmail: email
      })
    }

    // Optional: Redirect to intended page or trigger app state update
    // You might want to emit an event or update your Vue store here
    console.log('Token successfully acquired')
  }
}

// Handle page visibility changes for mobile Safari
const handleVisibilityChange = () => {
  if (!document.hidden && msalApp) {
    // Page became visible again, check if we have a valid account
    const account = msalApp.getAccount()
    const authInProgress = getItem(AUTH_STATE_KEY)

    console.log('Page visible - Account:', !!account, 'Auth in progress:', !!authInProgress)

    // If no account and no auth in progress, the auth might have been interrupted
    if (!account && !authInProgress) {
      console.log('No account found after page became visible')
      // You might want to check if the user was in the middle of authentication
      // and handle accordingly based on your app's flow
    }
  }
}

// Initialize visibility change handler
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', handleVisibilityChange)
}

// Utility function to check authentication state
export const isAuthenticationInProgress = () => {
  return !!getItem(AUTH_STATE_KEY)
}

// Utility function to get current account
export const getCurrentAccount = () => {
  if (!msalApp) {
    const authority = getItem(LAST_USED_AUTHORITY)
    msalApp = initUserAgent(authority)
  }
  return msalApp.getAccount()
}

// Utility function to clear all auth state (for debugging)
export const clearAuthState = () => {
  clearItem(AUTH_STATE_KEY)
  clearItem(FORCE_AUTH_CALLBACK)
  clearItem(LAST_USED_AUTHORITY)
  console.log('All authentication state cleared')
}

// Note: Visibility change handler will remain active for the application lifecycle
// This is acceptable since it's a lightweight handler for authentication state management
